import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { html } from 'lit';
import type { CompanyRegistration } from '../db/company-registration.js';
import {
  buildLoginLink,
  buildUserGreeting,
  companyFeedbackMailTemplate,
  companyRegistrationRejectedMailTemplate,
  companyRegistrationSubmittedMailTemplate,
  companyUserAddedMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from './mail-templates.js';

export const CompanyFeedbackMail = defineMailTemplate(MailTemplateType.COMPANY_FEEDBACK, {
  displayName: 'Bestätigung Gesprächsteilnahme (Unternehmen)',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: companyFeedbackMailTemplate,
  variables: [
    MailTemplateVars.COMPANY_FULL_NAME,
    MailTemplateVars.COMPANY_FIRST_NAME,
    MailTemplateVars.COMPANY_LAST_NAME,
    MailTemplateVars.LOGIN_LINK,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Unternehmensnutzer:innen<br />
    </p>
    <p>
      Wann:<br />
      - Am Tag nach durchgeführten Vorstellungsgesprächen, um die Teilnahme zu bestätigen
    </p>
  `,
  getMailData: (user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>, loginUrl: string) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.COMPANY_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.COMPANY_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.COMPANY_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.LOGIN_LINK]: buildLoginLink(loginUrl, 'Zu den Bewerbungen'),
      },
      scope: 'default',
    };
  },
});

export const CompanyUserAddedMail = defineMailTemplate(MailTemplateType.COMPANY_USER_ADDED, {
  displayName: 'Unternehmensnutzer:in freigeschaltet',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: companyUserAddedMailTemplate,
  variables: [
    MailTemplateVars.COMPANY_FULL_NAME,
    MailTemplateVars.COMPANY_FIRST_NAME,
    MailTemplateVars.COMPANY_LAST_NAME,
    MailTemplateVars.COMPANY_USERNAME,
    MailTemplateVars.LOGIN_LINK,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Unternehmensnutzer:innen<br />
    </p>
    <p>
      Wann:<br />
      - Wenn sie als neuer Benutzer freigeschaltet werden
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    loginUrl: string,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.COMPANY_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.COMPANY_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.COMPANY_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.COMPANY_USERNAME]: user.username ?? '',
        [MailTemplateVars.LOGIN_LINK]: buildLoginLink(loginUrl, 'Jetzt anmelden'),
      },
      scope: 'default',
    };
  },
});

export const CompanyRegistrationSubmittedMail = defineMailTemplate(
  MailTemplateType.COMPANY_REGISTRATION_SUBMITTED,
  {
    displayName: 'Neue Unternehmensregistrierung (Admin)',
    defaultTemplate: companyRegistrationSubmittedMailTemplate,
    variables: [
      MailTemplateVars.REGISTRATION_COMPANY_NAME,
      MailTemplateVars.REGISTRATION_CONTACT_NAME,
      MailTemplateVars.REGISTRATION_CONTACT_EMAIL,
    ],
    attachments: [],
    editorHint: () => html`
      <p>
        Wird gesendet an:<br />
        - Admin-E-Mail-Adresse<br />
      </p>
      <p>
        Wann:<br />
        - Wenn ein neues Unternehmen sich registriert
      </p>
    `,
    getMailData: (registration: Pick<CompanyRegistration, 'companyName' | 'contactFirstName' | 'contactLastName' | 'contactEmail'>) => {
      return {
        data: {
          [MailTemplateVars.REGISTRATION_COMPANY_NAME]: registration.companyName ?? '---',
          [MailTemplateVars.REGISTRATION_CONTACT_NAME]: `${registration.contactFirstName ?? ''} ${registration.contactLastName ?? ''}`.trim() || '---',
          [MailTemplateVars.REGISTRATION_CONTACT_EMAIL]: registration.contactEmail ?? '---',
        },
        scope: 'default',
      };
    },
  },
);

export const CompanyRegistrationRejectedMail = defineMailTemplate(
  MailTemplateType.COMPANY_REGISTRATION_REJECTED,
  {
    displayName: 'Unternehmensregistrierung abgelehnt',
    defaultTemplate: companyRegistrationRejectedMailTemplate,
    variables: [
      MailTemplateVars.REGISTRATION_COMPANY_NAME,
      MailTemplateVars.REGISTRATION_CONTACT_NAME,
      MailTemplateVars.REJECTION_REASON,
    ],
    attachments: [],
    editorHint: () => html`
      <p>
        Wird gesendet an:<br />
        - Kontaktperson des Unternehmens<br />
      </p>
      <p>
        Wann:<br />
        - Wenn die Registrierung abgelehnt wird
      </p>
    `,
    getMailData: (registration: Pick<CompanyRegistration, 'companyName' | 'contactFirstName' | 'contactLastName' | 'rejectionReason'>) => {
      return {
        data: {
          [MailTemplateVars.REGISTRATION_COMPANY_NAME]: registration.companyName ?? '---',
          [MailTemplateVars.REGISTRATION_CONTACT_NAME]: `${registration.contactFirstName ?? ''} ${registration.contactLastName ?? ''}`.trim() || '---',
          [MailTemplateVars.REJECTION_REASON]: registration.rejectionReason ?? '---',
        },
        scope: 'default',
      };
    },
  },
);
